import { useEffect, useMemo, useState } from "react";
import { Link, useSearch } from "wouter";
import { useFeed, useTables } from "../lib/store";
import { ago, byRank, descendantIds, formatDate, isDone, keyOf, spacesSorted, statusOf, since } from "../lib/select";
import { openItem, spaceHref } from "../lib/nav";
import { myWork, waitingOnMe } from "../lib/inbox";
import { milestoneWord } from "../lib/words";
import { Avatar, Empty, SpaceDot } from "../components/primitives";
import { StatusChip } from "../components/pickers";
import { PlanCard } from "../components/PlanCard";
import { describeEntry, eventNode, feedEntries, isWorkEvent } from "../components/activity";
import { currentMilestone } from "../components/FilterBar";
import { shipLine } from "./Overview";

/** Where a person starts the day: their own work first, then what waits on them, then what ships next. */
export function Home() {
  const t = useTables();
  const highlight = new URLSearchParams(useSearch()).get("plan");
  const [openPlans, setOpenPlans] = useState<Set<string>>(() => new Set(highlight ? [highlight] : []));
  useEffect(() => {
    if (!highlight) return;
    setOpenPlans((s) => new Set([...s, highlight]));
    requestAnimationFrame(() => document.getElementById(`plan-${highlight}`)?.scrollIntoView({ block: "center" }));
  }, [highlight]);

  const waiting = waitingOnMe(t);
  const work = myWork(t);
  const open = work.reduce((n, g) => n + g.items.length, 0);
  const late = work.find((g) => g.key === "late")?.items.length ?? 0;
  const feed = useFeed();
  const entries = useMemo(() => feedEntries(feed.filter(isWorkEvent), 12), [feed]);
  const today = new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
  const summary = [open ? `${open} open ${open === 1 ? "item" : "items"} on your plate` : "Nothing on your plate", late ? `${late} overdue` : "", waiting.count ? `${waiting.count} waiting on you` : ""].filter(Boolean).join(" · ");

  return (
    <div className="pad home">
      <div className="home-main">
        <div className="greet">
          <h1>{today}</h1>
          <p>{summary}.</p>
        </div>

        {waiting.count > 0 && (
          <section className="block" aria-labelledby="needs-h">
            <div className="block-h">
              <h3 id="needs-h">Waiting on you</h3>
              <span className="muted">{waiting.count}</span>
            </div>
            {waiting.questions.map((n) => (
              <button key={n.id} type="button" className="row" onClick={() => openItem(keyOf(t, n))}>
                <span className="stripe" style={{ background: "var(--signal)" }} />
                <Avatar actor={t.actors.get(n.blocker!.actorId)} size="sm" />
                <span className="t">
                  <span>{n.blocker!.kind === "needs_input" ? n.blocker!.reason : `Blocked: ${n.blocker!.reason}`}</span>
                  <small>
                    {n.title} · {t.actors.get(n.blocker!.actorId)?.name} asked {since(n.blocker!.at)}
                  </small>
                </span>
                <span className="chip sig">{n.blocker!.kind === "needs_input" ? "Answer" : "Unblock"}</span>
              </button>
            ))}
            {waiting.reviews.slice(0, 10).map((n) => (
              <button key={n.id} type="button" className="row" onClick={() => openItem(keyOf(t, n))}>
                <span className="stripe" style={{ background: "var(--primary-2)" }} />
                <SpaceDot space={t.spaces.get(n.spaceId)} />
                <span className="t">
                  <span>Review {n.title}</span>
                  <small>
                    {n.assignees
                      .filter((a) => a.role === "owner")
                      .map((a) => t.actors.get(a.actorId)?.name)
                      .filter(Boolean)
                      .join(", ") || "No owner"}{" "}
                    · sent {since(n.updatedAt)}
                  </small>
                </span>
                <span className="chip agent">Review</span>
              </button>
            ))}
            {waiting.plans.map((p) => {
              const expanded = openPlans.has(p.id);
              const count = [...t.nodes.values()].filter((n) => n.planId === p.id && n.draft).length;
              return (
                <div key={p.id} className="plan-row-wrap">
                  <button type="button" className="row" aria-expanded={expanded} onClick={() => setOpenPlans((s) => (s.has(p.id) ? new Set([...s].filter((x) => x !== p.id)) : new Set([...s, p.id])))}>
                    <span className="stripe" style={{ background: "var(--deco)" }} />
                    <Avatar actor={t.actors.get(p.proposedBy)} size="sm" />
                    <span className="t">
                      <span>{p.summary}</span>
                      <small>
                        {t.actors.get(p.proposedBy)?.name} proposed {count} {count === 1 ? "item" : "items"} in {t.spaces.get(p.spaceId)?.name}
                      </small>
                    </span>
                    <span className="chip">{expanded ? "Hide" : "Review plan"}</span>
                  </button>
                  {expanded && (
                    <div className="plan-row">
                      <PlanCard plan={p} highlight={p.id === highlight} />
                    </div>
                  )}
                </div>
              );
            })}
          </section>
        )}

        <section className="block" aria-labelledby="mine-h">
          <div className="block-h">
            <h3 id="mine-h">My work</h3>
            <span className="muted">{open || ""}</span>
          </div>
          {work.map((g) => (
            <div key={g.key} className="work-group" role="group" aria-label={g.label}>
              <div className={`work-group-h${g.key === "late" ? " late" : ""}`}>{g.label}</div>
              {g.items.slice(0, 12).map((n) => {
                const kind = t.kinds.get(n.kindId);
                const parent = n.parentId ? t.nodes.get(n.parentId) : undefined;
                return (
                  <button key={n.id} type="button" className="row" onClick={() => openItem(keyOf(t, n))}>
                    <SpaceDot space={t.spaces.get(n.spaceId)} />
                    <span className="t">
                      <span>{n.title}</span>
                      <small>
                        {t.spaces.get(n.spaceId)?.name}
                        {parent ? ` · ${parent.title}` : ""}
                      </small>
                    </span>
                    <StatusChip kind={kind} status={n.status} />
                    <span className={`due${g.key === "late" ? " late" : ""}`}>{n.dueDate ? formatDate(n.dueDate) : ""}</span>
                  </button>
                );
              })}
              {g.items.length > 12 && <div className="work-more muted">and {g.items.length - 12} more</div>}
            </div>
          ))}
          {!work.length && <Empty title="Nothing is assigned to you.">Work a producer gives you shows up here, soonest first.</Empty>}
        </section>
      </div>

      <div className="home-side">
        <ShippingNext />
        <section className="block feed" aria-labelledby="feed-h">
          <div className="block-h">
            <h3 id="feed-h">Recent activity</h3>
          </div>
          {entries.map((entry) => {
            const e = entry.first;
            const n = eventNode(t, e);
            return (
              <button key={entry.key} type="button" className="fi" onClick={() => n && t.nodes.has(n.id) && openItem(keyOf(t, n))} disabled={!n || !t.nodes.has(n.id)}>
                <Avatar actor={t.actors.get(e.actorId)} size="sm" />
                <span className="grow">
                  <b>{t.actors.get(e.actorId)?.name ?? "Someone"}</b> {describeEntry(t, entry)}
                  {e.via !== "web" && <span className="via">{e.via}</span>}
                </span>
                <span className="mono muted">{ago(e.createdAt)}</span>
              </button>
            );
          })}
          {!entries.length && <Empty title="No activity yet." />}
        </section>
      </div>
    </div>
  );
}

/** For each project, the update or milestone in progress, soonest first. */
function ShippingNext() {
  const t = useTables();
  const rows = spacesSorted(t)
    .map((s) => ({ s, m: currentMilestone(t, s.id) }))
    .filter((r): r is { s: (typeof r)["s"]; m: NonNullable<(typeof r)["m"]> } => Boolean(r.m))
    .sort((a, b) => (a.m.dueDate ?? "9999").localeCompare(b.m.dueDate ?? "9999") || byRank(a.s, b.s));
  if (!rows.length) return null;
  return (
    <section className="block" aria-labelledby="ship-h">
      <div className="block-h">
        <h3 id="ship-h">Shipping next</h3>
      </div>
      {rows.map(({ s, m }) => {
        const ids = descendantIds(t, m.id);
        const items = [...t.nodes.values()].filter((n) => ids.has(n.id) && !n.draft && t.kinds.get(n.kindId)?.key !== "phase");
        const done = items.filter((n) => isDone(t, n)).length;
        const line = shipLine(m, statusOf(t.kinds.get(m.kindId), m.status)?.category === "done", milestoneWord(t, s.id));
        return (
          <Link key={s.id} href={spaceHref(s.slug)} className="row ship-row">
            <SpaceDot space={s} />
            <span className="t">
              <span>{m.title}</span>
              <small>
                {s.name} · <span className={line.tone === "late" ? "late-text" : line.tone === "soon" ? "soon-text" : ""}>{line.text}</span>
              </small>
            </span>
            <span className="ms-meter" aria-label={`${done} of ${items.length} done`}>
              <span className="meter">
                <i style={{ width: `${items.length ? (done / items.length) * 100 : 0}%` }} />
              </span>
              <span className="mono">
                {done}/{items.length}
              </span>
            </span>
          </Link>
        );
      })}
    </section>
  );
}
